import { z } from 'zod';

/**
 * Kiasi HTTP contracts, transcribed from the service's OpenAPI description
 * (`crates/kiasi-api/src/http_axum/dispatch/openapi_schemas.rs`). Kiasi serves
 * `/openapi.json`; replace these with a generated `@thinkalpha/kiasi-ws-client`
 * once one is published (PLAT-12347).
 *
 * Prices are fixed-point integers scaled by {@link KIASI_PRICE_SCALE}.
 * Timestamps are Unix nanoseconds. Nanosecond values exceed Number's exact
 * integer range, so `first_ts`/`last_ts` can lose sub-microsecond precision in
 * JSON; bar opens on minute boundaries are multiples of 1e9 and stay exact.
 */

export const KIASI_PRICE_SCALE = 10_000;

/** Bar intervals the dev deployment advertises on `/compat` (2026-10-16). */
export const KiasiInterval = z.enum(['10s', '30s', '1m', '5m', '10m', '15m', '30m', '1h', '1d']);
export type KiasiInterval = z.infer<typeof KiasiInterval>;

export const KiasiProfileMode = z.enum(['volume', 'quote', 'session']);
export type KiasiProfileMode = z.infer<typeof KiasiProfileMode>;

export const KiasiAdjustmentMode = z.enum(['raw', 'splits', 'big_divs', 'total_return']);
export type KiasiAdjustmentMode = z.infer<typeof KiasiAdjustmentMode>;

const volume = z.int().nonnegative();
const scaledPrice = z.int();
const nanos = z.number();

export const KiasiPriceLevel = z.object({
    price: scaledPrice,
    bid: volume,
    ask: volume,
    mid: volume,
    pre: volume,
    reg: volume,
    post: volume,
    total: volume,
});
export type KiasiPriceLevel = z.infer<typeof KiasiPriceLevel>;

const histogramTotals = {
    total_vol: volume,
    vwap_volume: volume,
    vwap_notional: z.int(),
    /** `null` exactly when `vwap_volume` is 0. */
    vwap: scaledPrice.nullable(),
    poc: scaledPrice,
    vah: scaledPrice,
    val: scaledPrice,
    /** Ask volume minus bid volume. */
    delta: z.int(),
    bid_vol: volume,
    ask_vol: volume,
    mid_vol: volume,
    pre_vol: volume,
    reg_vol: volume,
    post_vol: volume,
};

/** `GET /bars/{symbol}/profile` response. */
export const KiasiVolumeProfile = z.object({
    mode: KiasiProfileMode,
    bar_count: volume,
    ...histogramTotals,
    /** Ascending price order. */
    levels: z.array(KiasiPriceLevel),
    adjust: KiasiAdjustmentMode,
});
export type KiasiVolumeProfile = z.infer<typeof KiasiVolumeProfile>;

/** One `GET /bars/{symbol}?projection=summary` row: a bar without its histogram. */
export const KiasiHistorySummaryRow = z.object({
    symbol: z.string(),
    interval: z.string(),
    bar_open_ts: nanos,
    /** `open`, `high`, `low` and `close` are all present or all `null`. */
    open: scaledPrice.nullable(),
    high: scaledPrice.nullable(),
    low: scaledPrice.nullable(),
    close: scaledPrice.nullable(),
    price_carried: z.boolean(),
    first_ts: nanos,
    last_ts: nanos,
    trade_count: volume,
    open_auction_vol: volume,
    close_auction_vol: volume,
    ...histogramTotals,
});
export type KiasiHistorySummaryRow = z.infer<typeof KiasiHistorySummaryRow>;

/** One element of the bare array `GET /bars/{symbol}` returns. */
export const KiasiVolumeBar = KiasiHistorySummaryRow.extend({
    levels: z.array(KiasiPriceLevel),
});
export type KiasiVolumeBar = z.infer<typeof KiasiVolumeBar>;

export const KiasiHistorySummaryReadReport = z.object({
    row_count: volume,
    ready_partition_count: volume,
    skipped_partition_count: volume,
    not_ready_partition_count: volume,
    includes_histogram_levels: z.boolean(),
    projection: z.string(),
    rows: z.array(KiasiHistorySummaryRow),
    adjust: KiasiAdjustmentMode,
});
export type KiasiHistorySummaryReadReport = z.infer<typeof KiasiHistorySummaryReadReport>;
